import React, { useCallback, useRef, useState } from 'react';
import { ButtonNewRecord, FlexRow, TableDataState, usePaginatedQueryV8 } from 'motion-components';

import { Button, Card } from 'react-bootstrap';
import { FaListAlt } from 'react-icons/fa';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { gqlGetQuery } from '../graphql';
import { buildWhereFromFilterValues, Filters } from './filters';
import { DataGrid } from './grid';
import { QuickView } from './quickview';
import { handleExport } from './export-columns';
import { useApolloClient } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { CollectionOrderStates } from 'shared-pages/shared/constants';
import { isCombatt, isFotokiklosi, isRebatteryLiion } from 'shared-pages/common/constants';
import { promiseModalSelectCollectionPoint } from 'shared-pages/components';
import { useCollectionOrderCreate } from '../form/use-collection-order-create';

/* ----------------------------------------------------------- */
/* ------------------------ Page ----------------------------- */
export const CollectionOrdersGridPage = observer(
  ({
    collectionPointId,
    isGridInForm = false,
  }: {
    collectionPointId?: number;
    isGridInForm?: boolean;
  }) => {
    const { appStore, tableStore } = useRootStore();
    const { handleGraphqlError } = useHandleError('CollectionORdersGridPage');

    const location = useLocation<any>();
    const history = useHistory();
    const [selected, setSelected] = useState<any>(null);
    const [showQuickView, setShowQuickView] = useState(false);
    const { t } = useTranslation();

    const { canView, canAddNew, canEdit, canDelete } = userPermissions();

    function userPermissions() {
      if (isFotokiklosi) {
        if (appStore.isPlatformOwner || appStore.isCollector)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else if (appStore.isCollectionPoint)
          return { canView: true, canAddNew: true, canEdit: false, canDelete: false };
        else return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
      } else if (isRebatteryLiion) {
        if (appStore.isPlatformOwner)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else if (appStore.isCollector)
          return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
        else if (appStore.isCollectionPoint)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
      } else if (isCombatt) {
        /* Combatt */
        if (appStore.isPlatformOwner)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else if (appStore.isCollector)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else if (appStore.isCollectionPoint)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
      } else {
        if (appStore.isPlatformOwner)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else if (appStore.isCollector)
          return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
        else return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
      }
    }

    const persistedValues = isGridInForm ? { collectionPointId } : {};
    const { quickCreateFromCollectionPoint } = useCollectionOrderCreate();
    const [quickCreateLoading, setQuickCreateLoading] = useState(false);
    /* ------------------- Query ------------------------------- */
    const initOrderBy = 'orderedDate DESC';
    const initialFilters = isGridInForm
      ? { isActive: true, collectionPointId: collectionPointId }
      : isRebatteryLiion
      ? { isActive: true, ...(location.state?.filterValues ?? {}) }
      : {
          isActive: true,
          statusId:
            appStore.isPlatformOwner || appStore.isCollector || appStore.isCollectorDriver
              ? [CollectionOrderStates.NotRouted, CollectionOrderStates.Routed]
              : [],
        };
    const query = usePaginatedQueryV8(
      gqlGetQuery,
      buildWhereFromFilterValues,
      location.state?.order ?? initOrderBy,
      appStore,
      true,
      initialFilters,
      !isGridInForm, // History Replace
      !isGridInForm ? undefined : 'coll_orders_grid',
      persistedValues,
    );
    const {
      loading,
      data,
      dataCount,
      error,
      initPageIndex,
      filtersChanged,
      gridPageChanged,
      queryState,
      queryParams,
      setQueryParams,
      refetch,
    } = query;

    const client = useApolloClient();

    const handleCreateNew = useCallback(() => {
      history.push({
        pathname: generatePath(getRouteByName('Order'), { id: -1 }),
        state: {
          pathname: location.pathname,
          ...location.state,
        },
      });
    }, [history, location.pathname, location.state]);

    /** Γρήγορη δημιουργία αιτήματος: επιλογή σημείου συλλογής → αυτόματη αποθήκευση */
    const handleQuickCreate = useCallback(async () => {
      const res = await promiseModalSelectCollectionPoint({ appStore, t });
      if (!res?.id) return;

      setQuickCreateLoading(true);
      try {
        const id = await quickCreateFromCollectionPoint(res.id);
        if (id) await refetch();
      } finally {
        setQuickCreateLoading(false);
      }
    }, [appStore, t, quickCreateFromCollectionPoint, refetch]);

    const gridRef = useRef();

    if (error) {
      handleGraphqlError('query-get', error);
      return <h3 className="text-center">{error.message}</h3>;
    } else {
      return (
        <Card className="shadow">
          {!isGridInForm && (
            <Card.Header>
              <FlexRow className="justify-content-between align-items-center">
                <Card.Title className="m-0">{t('Αιτήματα Συλλογής')}</Card.Title>

                <div className="d-flex gap-2">
                  {canAddNew && !isRebatteryLiion && (
                    <>
                      {!isRebatteryLiion && (
                        <ButtonNewRecord
                          onClick={handleQuickCreate}
                          size="sm"
                          label={t('Γρήγορο Αίτημα')}
                          disabled={quickCreateLoading}
                        />
                      )}
                      <ButtonNewRecord
                        onClick={handleCreateNew}
                        size="sm"
                        label={t('Νέο Αίτημα Συλλογής')}
                      />
                    </>
                  )}
                  <Button
                    size="sm"
                    variant={showQuickView ? 'dark' : 'outline-secondary'}
                    onClick={() => setShowQuickView(!showQuickView)}
                    className="d-flex align-items-center gap-2"
                    style={{
                      fontWeight: showQuickView ? 600 : 400,
                      minWidth: '140px',
                      borderWidth: '1.5px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <FaListAlt size={16} />
                    {showQuickView ? 'Απόκρυψη' : 'Λεπτομέρειες'}
                  </Button>
                </div>
              </FlexRow>
            </Card.Header>
          )}
          <Card.Body className="nopadding">
            {/* Φίλτρα */}
            <Filters
              onChange={filtersChanged}
              initialValues={queryState?.filterValues ?? initialFilters}
              disable={tableStore.state !== TableDataState.browse}
              isGridInForm={isGridInForm}
            />

            {/* Grid */}
            <DataGrid
              ref={gridRef}
              loading={loading}
              data={data}
              dataCount={dataCount}
              initPageIndex={initPageIndex}
              onPageChange={gridPageChanged}
              handleExport={() => {
                handleExport(client, queryState, t);
              }}
              initOrderBy={queryParams?.order ?? initOrderBy}
              onOrderChange={(orderBy) => {
                const newQueryParams = { ...queryParams, order: orderBy };
                setQueryParams(newQueryParams);
                if (!isGridInForm) {
                  history.replace({
                    pathname: location.pathname,
                    state: {
                      ...location.state,
                      order: orderBy,
                    },
                  });
                }
              }}
              selected={selected}
              setSelected={setSelected}
              refetch={refetch}
              isGridInForm={isGridInForm}
            />

            {/* Quick View */}
            <QuickView
              show={showQuickView}
              onHide={() => setShowQuickView(false)}
              selected={selected}
            />
          </Card.Body>
        </Card>
      );
    }
  },
);
